import {useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react';
import type {KeyboardEvent} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import type {
    ColDef,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    PostSortRowsParams,
    RowHeightParams,
    SuppressHeaderKeyboardEventParams,
    SuppressKeyboardEventParams,
    ValueGetterParams
} from 'ag-grid-community';
import {CircleQuestionMark, PencilIcon, Trash2Icon} from 'lucide-react';
import _ from 'lodash';
import {AppGrid, HEADER_HEIGHT} from 'components/common/ag-grid/AppGrid.component';
import {useAvailableHeight} from 'components/common/ag-grid/useAvailableHeight';
import {Button, ButtonType} from 'components/common/Button.component';
import Select from 'components/common/select/Select.component';
import {ICON_SIZE, KEY} from 'components/common/constants';
import {Loader, LoaderSize} from 'components/common/Loader.component';
import {Tooltip} from 'components/tooltip/Tooltip';
import {HEADING_INFO_TOOLTIP_PLACE, ROW_TOOLTIP_PLACE, SHARED_TOOLTIP_ID} from 'components/tooltip/AppTooltip.component';
import {useModalContext} from 'components/modals/ModalContext';
import {useTranslation} from 'components/translations/useTranslation';
import {launchDarkly} from 'utils/launchDarkly';
import {LOADING_STATUS, MODAL_ACTIONS, MODAL_SEVERITY} from 'utils/constants';
import {handleError} from 'utils/errorHandle.function';
import * as Api from 'utils/api/api';
import type {Translate} from 'types/myTasks';
import type {
    GeneralApproverLine,
    GeneralApproversResponse,
    GeneralApproversResult,
    GeneralApproverUpdate,
    ProjectDimension,
    ProjectDraft,
    ProjectDraftStore,
    ProjectGridRow,
    ProjectOption
} from 'types/projects';
import UserSearch from '../input/UserSearch.component';
import * as reduxSelectors from '../store/application.reducers';
import Projects from './Projects.component';
import {getProjectsUsersAndRoles} from './projects.action';
import './newProjects.scss';

// swaps the classic list in Projects.component for the ag-grid rendering below
const AG_GRID_IN_CONFIGURATION_FLAG = 'fe-use-ng-grid-in-configuration';

/**
 * Row id of the draft row the Add button opens. A line that does not exist yet has no id of
 * its own, and only one draft is ever open, so a constant is all it needs.
 */
const NEW_ROW_ID = 'new-project-row';

// the columns the grid draws after the project units, in the order the classic list had them
const ROLE_COL = 'role';
const APPROVERS_COL = 'approvers';
const ACTIONS_COL = 'actions';

/** One project unit column. There is one per unit the company keeps, so the id is positional. */
const dimensionCol = (index: number) => `dimension-${index}`;

/**
 * The value the Any option carries, as the classic list's own option had it: the line is left
 * open to every value of that unit.
 */
const ANY_VALUE = 0;

// one line of text, with room for the ghost buttons beside it
const ROW_HEIGHT = 52;

// the row being edited carries pickers, which stand 40px tall of their own
const EDITING_ROW_HEIGHT = 52;

// the grid fills the page, keeping below it the Add button (40px, and the 24px it stands off the
// grid), the card's own padding and the page's bottom margin
const GRID_HEIGHT = Object.freeze({bottomGap: 112, watchSelector: '.new-projects__card'});

// wide enough for the Save and Cancel buttons a row in edit mode wears
const ACTIONS_WIDTH = 180;

// a unit's own name is short, but the values it takes are not - and there is a column per unit
const DIMENSION_MIN_WIDTH = 140;
const ROLE_MIN_WIDTH = 160;
const APPROVERS_MIN_WIDTH = 200;

// as long a list of values as a picker shows before it scrolls
const MENU_MAX_HEIGHT = 200;

// the Add button, so focus can go back to it once a draft row is done with
const ADD_BUTTON_ID = 'projects-add-button';

// how many frames a row is given to be drawn again before focus gives up on it (see returnFocus)
const FOCUS_RENDER_ATTEMPTS = 5;

/** The Edit button of a row, so focus can go back to it once that row's editor closes. */
const editButtonId = (rowId: string) => `projects-edit-button-${rowId}`;

// the tab order below is these, in the order the page draws them. A disabled button is no stop,
// which is every button on the page a system viewer reads - and the Save of an incomplete line
const SORTABLE_HEADERS = '.ag-header-cell-sortable';
const ACTION_BUTTONS = '.new-projects__actions button:not(:disabled)';
// the pickers the row being edited puts in its cells, ahead of that row's own buttons
const EDITOR_INPUTS = '.new-projects__editor input:not([disabled])';
const ROW_CONTROLS = `${EDITOR_INPUTS}, ${ACTION_BUTTONS}`;

/**
 * The list a picker has open, which is drawn over the page rather than in the cell - see the
 * pickers below. It is what tells an Escape that closed a list from one meant for the row.
 */
const OPEN_PICKER_LIST = '.ga-select__menu';

/**
 * The grid of a company with no project units: nothing but the message saying so. ag-grid leaves
 * a 1px strip where the header row would be, which the stylesheet takes away under this class.
 */
const HEADLESS_GRID = 'new-projects__grid new-projects__grid--headless';

/**
 * Takes Tab off the heading the tab order leaves the header row from - the last one that sorts,
 * and so the last one worth standing on. The grid would walk on to the actions heading, which
 * does nothing, and then loop back to the first, which is a ring focus never gets out of;
 * onGridKeyDown carries it into the rows instead. Shift-Tab is left alone - walking back up the
 * headings is what it should do.
 */
const tabOutOfTheHeaderIsNotTheGrids = ({event}: SuppressHeaderKeyboardEventParams<ProjectGridRow>) =>
    event.key === KEY.TAB && !event.shiftKey;

/**
 * Keeps the draft row under the saved lines, wherever the sort would otherwise put it: its cells
 * are empty, so any column would carry it to whichever end the empty value belongs at.
 */
function keepDraftLast({nodes}: PostSortRowsParams<ProjectGridRow>) {
    const draft = nodes.findIndex(node => node.data?.id === NEW_ROW_ID);
    if (draft !== -1) {
        nodes.push(...nodes.splice(draft, 1));
    }
}

/** The label a project unit value shows: its own name, or Any where the line takes them all. */
function dimensionLabel(dimension: ProjectDimension | undefined, translate: Translate): string {
    return dimension?.name || translate('projects.any');
}

/**
 * The order the classic list put the lines in: by the first project unit, a line open to every
 * value of it standing under the sentinel below - which, being uppercase, sorts ahead of most
 * names. Third-clicking a heading is what returns the grid to this order.
 */
function sortLines(lines: GeneralApproverLine[]): GeneralApproverLine[] {
    return _.sortBy(lines, line => line.dimensions[0]?.name ?? 'ANY');
}

/** The values each unit can be narrowed to, in header order, with Any at the end of every list. */
function buildDimensionOptions(result: GeneralApproversResult, translate: Translate): ProjectOption[][] {
    return result.headers.map(header => {
        const values = result.possibleValues?.[header.mdmGuid] ?? [];
        const options: ProjectOption[] = values.map(value => ({value: value.mdmGuid, label: value.name}));
        options.push({value: ANY_VALUE, label: translate('projects.any')});
        return options;
    });
}

/** Every approver on the list, once each: the ids the page has to have names looked up for. */
function approverIds(lines: GeneralApproverLine[]): number[] {
    return _.uniq(_.flatMap(lines, line => line.approvers));
}

/** The options the projects slice keeps, as a name per id - see the selectors below. */
function namesById(options: ProjectOption[] | null): Map<number, string> {
    return new Map((options ?? []).map(option => [Number(option.value), option.label]));
}

/**
 * The rows the grid draws: the saved lines, and the draft under them - which is where the
 * classic list put its new row.
 */
function buildRowData(lines: GeneralApproverLine[],
                      roleNames: Map<number, string>,
                      approverNames: Map<number, string>,
                      translate: Translate,
                      showNewRow: boolean): ProjectGridRow[] {

    const rows: ProjectGridRow[] = lines.map(line => ({
        id: String(line.id),
        line,
        dimensions: line.dimensions.map(dimension => dimensionLabel(dimension, translate)),
        roleName: (line.idRole !== null && roleNames.get(line.idRole)) || '',
        // the endpoint hands back ids alone; a name still on its way leaves nothing to list
        approvers: line.approvers.map(id => approverNames.get(id)).filter(Boolean).join(', ')
    }));

    return showNewRow
        ? [...rows, {id: NEW_ROW_ID, line: null, dimensions: [], roleName: '', approvers: ''}]
        : rows;
}

/** The draft the cells of the row being edited share - see ProjectDraftStore for why. */
function createDraftStore(initial: ProjectDraft): ProjectDraftStore {
    let draft = initial;
    const listeners = new Set<() => void>();

    return {
        getDraft: () => draft,
        change: patch => {
            draft = {...draft, ...patch};
            listeners.forEach(listener => listener());
        },
        subscribe: listener => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        }
    };
}

/** What every picker in the row being edited is given. */
interface EditorProps {
    store: ProjectDraftStore;
    translate: Translate;
}

/**
 * The picker for one project unit. It keeps the value it shows itself, so a pick never
 * re-renders the page - which would rebuild the grid's columns and take this picker down with
 * them. What the row is to save goes into the shared draft instead.
 */
const DimensionEditor = ({store, index, options, autoFocus, translate}: EditorProps & {
    index: number,
    options: ProjectOption[],
    autoFocus: boolean
}) => {
    const [value, setValue] = useState<ProjectOption>(() => {
        const dimension = store.getDraft().dimensions[index];
        const picked = dimension?.mdmGuid ?? ANY_VALUE;
        // the option itself where the line's value is one the unit still offers, so the list
        // opens on it - and the line's own value where the master data no longer carries it
        return options.find(option => option.value === picked)
            ?? {value: picked, label: dimensionLabel(dimension, translate)};
    });

    const change = useCallback((option: ProjectOption | null) => {
        if (!option) {
            return;
        }
        setValue(option);
        const dimensions = store.getDraft().dimensions.slice();
        dimensions[index] = {mdmGuid: option.value, name: option.label};
        store.change({dimensions});
    }, [store, index]);

    return (
        <Select<ProjectOption>
            id={`projects-dimension-${index}`}
            options={options}
            value={value}
            onChange={change}
            isClearable={false}
            isSearchable={true}
            autoFocus={autoFocus}
            // Tab walks the row's controls (see onGridKeyDown) rather than picking what it lands on
            tabSelectsValue={false}
            maxMenuHeight={MENU_MAX_HEIGHT}
            menuPlacement="auto"
            // the grid's body scrolls and clips, so the list is drawn over the page instead -
            // against the viewport, which is what a fixed position measures from
            menuPortalTarget={document.body}
            menuPosition="fixed"
            translate={translate}
        />
    );
};

/** The picker for the role the line's approvers hold. */
const RoleEditor = ({store, options, autoFocus, translate}: EditorProps & {
    options: ProjectOption[],
    autoFocus: boolean
}) => {
    const [value, setValue] = useState<ProjectOption | null>(
        () => options.find(option => option.value === store.getDraft().idRole) ?? null);

    const change = useCallback((option: ProjectOption | null) => {
        if (!option) {
            return;
        }
        setValue(option);
        store.change({idRole: Number(option.value)});
    }, [store]);

    return (
        <Select<ProjectOption>
            id="projects-role"
            options={options}
            value={value}
            onChange={change}
            isClearable={false}
            isSearchable={true}
            autoFocus={autoFocus}
            tabSelectsValue={false}
            maxMenuHeight={MENU_MAX_HEIGHT}
            menuPlacement="auto"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            translate={translate}
        />
    );
};

/** The picker for the users holding the role, which searches the company's users as it is typed. */
const ApproversEditor = ({store, translate}: EditorProps) => {
    const [value, setValue] = useState<ProjectOption[]>(() => store.getDraft().approvers);

    const change = useCallback((selected: ProjectOption[] | null) => {
        const approvers = Array.isArray(selected) ? selected : [];
        setValue(approvers);
        store.change({approvers});
    }, [store]);

    return (
        <UserSearch propagateValue={change}
                    includeSameUser={true}
                    value={value}
                    // the first cell of the row is the one that takes focus
                    stopFocus={true}
                    multi={true}
                    tabSelectsValue={false}
                    maxMenuHeight={MENU_MAX_HEIGHT}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    translate={translate}/>
    );
};

/**
 * The Save and Cancel of the row being edited. It listens to the draft the row's pickers write
 * to, so Save turns on the moment the line is one the endpoint will take - and while it is off,
 * says what the line is still missing.
 */
const RowEditorActions = ({store, rowId, validate, save, cancel, translate}: EditorProps & {
    rowId: string,
    validate: (draft: ProjectDraft, rowId: string) => string | null,
    save: () => void,
    cancel: () => void
}) => {
    const draft = useSyncExternalStore(store.subscribe, store.getDraft);
    const problem = validate(draft, rowId);
    const tooltipId = `projects-save-tooltip-${rowId}`;

    return (
        <span className="new-projects__actions">
            {/* the hint hangs off the wrapper: a disabled button takes no pointer events of its own */}
            <span data-for={SHARED_TOOLTIP_ID}
                  data-tip={problem ? tooltipId : undefined}
                  data-place={ROW_TOOLTIP_PLACE}>
                <Button type={ButtonType.SECONDARY}
                        onClick={save}
                        disabled={!!problem}
                        text={translate('projects.save')}
                />
            </span>
            {problem && <Tooltip id={tooltipId} content={() => problem}/>}
            {/* the label the classic row wore too - projects has no cancel of its own */}
            <Button type={ButtonType.SECONDARY}
                    onClick={cancel}
                    text={translate('roles.cancel')}
            />
        </span>
    );
};

/**
 * ag-grid rendering of the Configuration -> Projects page, behind
 * fe-use-ng-grid-in-configuration. The classic list it replaces is in Projects.component.
 */
export const NewProjects = () => {

    const {translate} = useTranslation();
    const dispatch = useDispatch();
    // every popup this page opens is the modal router's
    const toggleModal = useModalContext()?.toggleModal;

    const currentContextId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const userRoles = useSelector(reduxSelectors.getUsersRoles);

    /**
     * The roles a line's approvers can hold, and the names behind the approver ids the lines
     * carry. Both are the projects slice's, filled by the page's own action - which is where the
     * classic list read them from too, a row at a time.
     */
    const projectRoles: ProjectOption[] | null = useSelector(reduxSelectors.getProjectsRoles);
    const projectUsers: ProjectOption[] | null = useSelector(reduxSelectors.getProjectsUsers);

    const [result, setResult] = useState<GeneralApproversResult | null>(null);
    const [loadingStatus, setLoadingStatus] = useState<number>(LOADING_STATUS.LOADING);

    // the draft row, and the row whose cells are open for editing - which is the draft while it
    // is being filled in. Only one row is ever in edit mode, as in the classic list
    const [showNewRow, setShowNewRow] = useState(false);
    const [editingRowId, setEditingRowId] = useState<string | null>(null);

    /** What the pickers of the row being edited have put in so far - see ProjectDraftStore. */
    const draftStore = useRef<ProjectDraftStore | null>(null);

    // the loader stands in the grid's place until the lines land, and the two are measured as one
    // box, so the card keeps its height when the rows arrive. Whichever of them is mounted is the
    // element measured, which is why it arrives through a callback
    const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
    const gridHeight = useAvailableHeight(gridElement, GRID_HEIGHT);

    const gridApi = useRef<GridApi<ProjectGridRow> | null>(null);
    const onGridReady = useCallback((event: GridReadyEvent<ProjectGridRow>) => {
        gridApi.current = event.api;
    }, []);

    // a system viewer who is not also an administrator only ever reads this page
    const readOnly = !!userRoles?.systemViewer && !userRoles?.systemAdministrator;

    /**
     * The company the request in flight was made for. A response is only taken up while that is
     * still the company in context: the slower answer to a company just left would otherwise
     * land on top of the one now on screen.
     */
    const requestedContextId = useRef(currentContextId);

    const getData = useCallback(() => {
        requestedContextId.current = currentContextId;
        setLoadingStatus(LOADING_STATUS.LOADING);
        Api.getGeneralApprovers(currentContextId).then((response: GeneralApproversResponse) => {
            if (requestedContextId.current !== currentContextId) {
                return;
            }
            setResult(response.result);
            setShowNewRow(false);
            setEditingRowId(null);
            draftStore.current = null;
            setLoadingStatus(LOADING_STATUS.DONE);
            // the roles, and a name for every approver the lines name by id
            dispatch(getProjectsUsersAndRoles(currentContextId, approverIds(response.result.lines)));
        }, (error: unknown) => {
            if (requestedContextId.current !== currentContextId) {
                return;
            }
            setLoadingStatus(LOADING_STATUS.ERROR);
            handleError(error);
        });
    }, [currentContextId, dispatch]);

    // loads on mount, and starts over whenever the company context changes - as the wrapper the
    // classic page extended did. The lines of the company being left go with it, before the new
    // ones are even asked for: they are that company's rows, but the Save and the Delete under
    // them would reach the company now in context, so none of them may stay to be pressed
    useEffect(() => {
        setResult(null);
        setShowNewRow(false);
        setEditingRowId(null);
        draftStore.current = null;
        getData();
    }, [getData]);

    const headers = useMemo (() => result?.headers ?? [], [result]);
    const lines = useMemo(() => sortLines(result?.lines ?? []), [result]);
    const dimensionOptions = useMemo(
        () => result ? buildDimensionOptions(result, translate) : [], [result, translate]);
    // memoised rather than read straight off the selectors: these feed the column definitions,
    // and a new array there would rebuild the columns - and the open pickers with them
    const roleOptions = useMemo<ProjectOption[]>(() => projectRoles ?? [], [projectRoles]);
    const roleNames = useMemo(() => namesById(projectRoles), [projectRoles]);
    const approverNames = useMemo(() => namesById(projectUsers), [projectUsers]);

    /**
     * The row whose editor has just closed. Save and Cancel unmount themselves as they are
     * pressed, which would drop keyboard focus onto the document body - this is what says where
     * it should land instead.
     */
    const returnFocusTo = useRef<string | null>(null);

    /** Leaves edit mode, dropping the draft row with it. */
    const cancelEdit = useCallback(() => {
        returnFocusTo.current = editingRowId;
        draftStore.current = null;
        setEditingRowId(null);
        setShowNewRow(false);
    }, [editingRowId]);

    const enterEditMode = useCallback((row: ProjectGridRow) => {
        const line = row.line;
        if (!line) {
            return;
        }
        draftStore.current = createDraftStore({
            // the line's own values, to be handed back untouched wherever the user changes nothing
            dimensions: line.dimensions.map(dimension => ({...dimension})),
            idRole: line.idRole,
            approvers: line.approvers.map(id => ({value: id, label: approverNames.get(id) ?? ''}))
        });
        setEditingRowId(row.id);
        // only one row is ever in edit mode: the Edit buttons are locked while the draft row is
        // open, and this keeps that true should a row ever be opened some other way
        setShowNewRow(false);
    }, [approverNames]);

    const addRow = useCallback(() => {
        draftStore.current = createDraftStore({
            // every unit open to any of its values, which is where the classic new row started
            dimensions: headers.map(() => ({mdmGuid: null, name: null})),
            idRole: null,
            approvers: []
        });
        setShowNewRow(true);
        setEditingRowId(NEW_ROW_ID);
    }, [headers]);

    const saveRow = useCallback(() => {
        const store = draftStore.current;
        const rowId = editingRowId;
        if (!store || rowId === null || !result) {
            return;
        }

        const draft = store.getDraft();
        const line = rowId === NEW_ROW_ID ? null : lines.find(saved => String(saved.id) === rowId);
        cancelEdit();

        const update: GeneralApproverUpdate = {
            dimensions: draft.dimensions.map(dimension => dimension.mdmGuid),
            generalApprovers: draft.approvers.map(approver => Number(approver.value)),
            headerVersion: result.version,
            id: line ? line.id : null,
            idRole: draft.idRole,
            lineVersion: line ? line.version : null
        };

        Api.updateGeneralApprovers(update, currentContextId).then(getData, (error: unknown) => {
            handleError(error);
            // whatever the endpoint refused the save over, the list it still holds is the truth
            getData();
        });
    }, [editingRowId, result, lines, cancelEdit, currentContextId, getData]);

    const deleteRow = useCallback((row: ProjectGridRow) => {
        const line = row.line;
        if (!line) {
            return;
        }
        toggleModal?.(MODAL_ACTIONS.CONFIRM_ACTION, {
            title: translate('popUp.deleteRowTitle'),
            description: translate('projects.confirmDelete'),
            confirmButtonText: 'popUp.delete',
            cancelButtonText: 'popUp.cancel',
            severity: MODAL_SEVERITY.WARNING,
            handleAction: () => {
                toggleModal(null);
                Api.deleteGeneralApprovers(line.id, currentContextId).then(getData, (error: unknown) => {
                    handleError(error);
                    getData();
                });
            }
        });
    }, [toggleModal, translate, currentContextId, getData]);

    /**
     * What the line is still missing before it can be saved, as the classic row's own check had
     * it: no two lines may carry the same project units and role, and a line needs both a role
     * and someone to hold it.
     *
     * The units are compared as they read rather than by their ids, so a line left open to any
     * value counts as the same as a saved one that already is - a pair the classic check missed,
     * the saved line carrying no value at all where the drafted one carries Any.
     */
    const validate = useCallback((draft: ProjectDraft, rowId: string): string | null => {
        const drafted = draft.dimensions.map(dimension => dimensionLabel(dimension, translate));
        const duplicate = lines.some(line => String(line.id) !== rowId
            && line.idRole === draft.idRole
            && _.isEqual(line.dimensions.map(dimension => dimensionLabel(dimension, translate)), drafted));

        if (duplicate) {
            return translate('projects.selectCombination');
        }
        if (!draft.idRole) {
            return translate('projects.selectRole');
        }
        if (draft.approvers.length === 0) {
            return translate('projects.selectRoleHolder');
        }
        return null;
    }, [lines, translate]);

    const rowData = useMemo(
        () => buildRowData(lines, roleNames, approverNames, translate, showNewRow),
        [lines, roleNames, approverNames, translate, showNewRow]);

    // the draft is appended, so on a list longer than the grid the Add button would otherwise
    // look like it did nothing
    useEffect(() => {
        if (showNewRow) {
            gridApi.current?.ensureIndexVisible(rowData.length - 1);
        }
    }, [showNewRow, rowData.length]);

    /** The row being edited stands taller than the others, to hold its pickers. */
    const getRowHeight = useCallback((params: RowHeightParams<ProjectGridRow>) =>
        params.data?.id === editingRowId ? EDITING_ROW_HEIGHT : ROW_HEIGHT, [editingRowId]);

    // a row's height is measured once, so the grid has to be told to measure them again
    useEffect(() => {
        gridApi.current?.resetRowHeights();
    }, [editingRowId, rowData]);

    /**
     * Focuses the Edit button of a row, waiting for the row to be drawn again if it has to: the
     * cell still holds the editor's own buttons for a frame after the row leaves edit mode, and
     * the Edit button that replaces them is only there once ag-grid has drawn the cell again.
     */
    const returnFocus = useCallback((rowId: string, attempts = FOCUS_RENDER_ATTEMPTS) => {
        const edit = document.getElementById(editButtonId(rowId));
        if (edit) {
            edit.focus();
        } else if (attempts > 0) {
            requestAnimationFrame(() => returnFocus(rowId, attempts - 1));
        }
    }, []);

    /**
     * Hands keyboard focus back once an editor closes: to the Edit button of the row it closed
     * on, which is where the tab order stood when that row was opened - or to the Add button,
     * when the row that closed was the draft and so is no longer there to focus.
     */
    useEffect(() => {
        const rowId = returnFocusTo.current;
        if (editingRowId !== null || rowId === null) {
            return;
        }
        returnFocusTo.current = null;

        if (!gridApi.current?.getRowNode(rowId)) {
            document.getElementById(ADD_BUTTON_ID)?.focus();
            return;
        }
        returnFocus(rowId);
    }, [editingRowId, rowData, returnFocus]);

    /**
     * The tab order inside the grid: the headings that sort - every one but the actions - and
     * then the controls the rows carry, which are the Edit and Delete buttons of a row, and the
     * pickers with the Save and Cancel buttons of the row being edited.
     *
     * What the grid and the browser would do between them is walk every heading, the actions one
     * that does not sort included, in a ring, and stop on each row before the controls it carries
     * - the row is a tab stop of the grid's own making (see AppGrid), and standing on one does
     * nothing. Every step below is one of those skipped: the last sortable heading hands focus to
     * the first control, and the last control of a row hands it to the first control of the row
     * under it. The last control of the last rendered row is left to the browser, which carries
     * focus out of the grid and on to the Add button below it - where it should go.
     *
     * Escape gives up the row being edited, as it gave up the classic list's new row - unless a
     * picker had a list open, which is what that Escape closed. A picker answers the key either
     * way and prevents it either way, so the list still standing is what tells the two apart:
     * the first Escape closes the list, and the second gives up the row.
     */
    const onGridKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === KEY.ESCAPE) {
            if (editingRowId !== null && !document.querySelector(OPEN_PICKER_LIST)) {
                cancelEdit();
            }
            return;
        }

        if (event.key !== KEY.TAB || event.defaultPrevented) {
            return;
        }

        const grid = event.currentTarget;
        const controls = Array.from(grid.querySelectorAll<HTMLElement>(ROW_CONTROLS));
        if (controls.length === 0) {
            return;
        }

        const headings = Array.from(grid.querySelectorAll<HTMLElement>(SORTABLE_HEADERS));
        const lastHeading = headings[headings.length - 1];
        const target = event.target as HTMLElement;

        const moveTo = (element: HTMLElement | undefined) => {
            if (!element) {
                return;
            }
            element.focus();
            event.preventDefault();
        };

        if (target === lastHeading && !event.shiftKey) {
            moveTo(controls[0]);
            return;
        }

        const index = controls.indexOf(target);
        if (index === -1) {
            return;
        }

        if (event.shiftKey) {
            // back off the first control, and the headings pick the walk up again
            moveTo(index === 0 ? lastHeading : controls[index - 1]);
        } else if (index < controls.length - 1) {
            moveTo(controls[index + 1]);
        }
    }, [editingRowId, cancelEdit]);

    const columnDefs = useMemo<ColDef<ProjectGridRow>[]>(() => {
        const editLabel = translate('workflows.edit');
        const deleteLabel = translate('workflows.delete');
        const roleLabel = translate('projects.approverRole');
        const approverLabel = translate('projects.approverEmail');

        /**
         * Which keys the cell's own content owns rather than the grid.
         *
         * Tab always: the buttons and the pickers a cell holds are in the document's tab order,
         * and leaving Tab to the browser is what walks focus through them - the grid would jump a
         * whole cell instead. And every key in a row being edited, so a picker keeps its caret
         * and its list rather than having Enter, Escape and the arrows read as navigation.
         */
        const cellOwnsKey = (params: SuppressKeyboardEventParams<ProjectGridRow>) =>
            params.event.key === KEY.TAB || params.data?.id === editingRowId;

        /** Whether this is the row being edited, and so the row that draws pickers. */
        const isEditing = (row: ProjectGridRow) => row.id === editingRowId && !!draftStore.current;

        const columns: ColDef<ProjectGridRow>[] = headers.map((header, index) => ({
            colId: dimensionCol(index),
            headerName: header.name,
            // a narrowed column clips its heading; the tooltip is what spells it out again
            headerTooltip: header.name,
            flex: 1,
            minWidth: DIMENSION_MIN_WIDTH,
            // every line is loaded, so the grid is free to order them - and the third click on a
            // heading returns the rows to the order the endpoint handed them over in
            sortable: true,
            valueGetter: (params: ValueGetterParams<ProjectGridRow>) => params.data?.dimensions[index],
            // ag-grid cannot spot truncation inside a cell renderer, so the tooltip is asked for
            // explicitly
            tooltipValueGetter: params => params.data?.dimensions[index] || undefined,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<ProjectGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                if (!isEditing(row)) {
                    return row.dimensions[index];
                }
                return (
                    <span className="new-projects__editor" data-cy={`projects.dimension-${index}`}>
                        <DimensionEditor store={draftStore.current!}
                                         index={index}
                                         options={dimensionOptions[index] ?? []}
                                         // the row takes focus at its first cell
                                         autoFocus={index === 0}
                                         translate={translate}/>
                    </span>
                );
            }
        }));

        columns.push({
            colId: ROLE_COL,
            headerName: roleLabel,
            headerTooltip: roleLabel,
            flex: 1,
            minWidth: ROLE_MIN_WIDTH,
            sortable: true,
            valueGetter: (params: ValueGetterParams<ProjectGridRow>) => params.data?.roleName,
            tooltipValueGetter: params => params.data?.roleName || undefined,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<ProjectGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                if (!isEditing(row)) {
                    return row.roleName;
                }
                return (
                    <span className="new-projects__editor" data-cy="projects.role">
                        <RoleEditor store={draftStore.current!}
                                    options={roleOptions}
                                    // a company with no project units opens the row here instead
                                    autoFocus={headers.length === 0}
                                    translate={translate}/>
                    </span>
                );
            }
        }, {
            colId: APPROVERS_COL,
            headerName: approverLabel,
            headerTooltip: approverLabel,
            // the approvers are a list where every other column holds a single value
            flex: 2,
            minWidth: APPROVERS_MIN_WIDTH,
            sortable: true,
            valueGetter: (params: ValueGetterParams<ProjectGridRow>) => params.data?.approvers,
            tooltipValueGetter: params => params.data?.approvers || undefined,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<ProjectGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                if (!isEditing(row)) {
                    return row.approvers;
                }
                return (
                    <span className="new-projects__editor" data-cy="projects.approvers">
                        <ApproversEditor store={draftStore.current!} translate={translate}/>
                    </span>
                );
            }
        }, {
            colId: ACTIONS_COL,
            headerName: translate('projects.actions'),
            type: 'rightAligned',
            width: ACTIONS_WIDTH,
            minWidth: ACTIONS_WIDTH,
            maxWidth: ACTIONS_WIDTH,
            sortable: false,
            resizable: false,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<ProjectGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }

                if (isEditing(row)) {
                    return (
                        <RowEditorActions store={draftStore.current!}
                                          rowId={row.id}
                                          validate={validate}
                                          save={saveRow}
                                          cancel={cancelEdit}
                                          translate={translate}/>
                    );
                }

                return (
                    <span className="new-projects__actions">
                        <Button id={editButtonId(row.id)}
                                type={ButtonType.GHOST}
                                iconOnly={true}
                                icon={<PencilIcon size={ICON_SIZE.MEDIUM}/>}
                                // one row is edited at a time, as the classic list had it
                                disabled={readOnly || editingRowId !== null}
                                onClick={() => enterEditMode(row)}
                                ariaLabel={editLabel}
                                tooltip={{id: `projects-edit-tooltip-${row.id}`, content: () => editLabel}}
                        />
                        <Button type={ButtonType.GHOST}
                                iconOnly={true}
                                icon={<Trash2Icon size={ICON_SIZE.MEDIUM}/>}
                                disabled={readOnly || editingRowId !== null}
                                onClick={() => deleteRow(row)}
                                ariaLabel={deleteLabel}
                                tooltip={{id: `projects-delete-tooltip-${row.id}`, content: () => deleteLabel}}
                        />
                    </span>
                );
            }
        });

        // the heading Tab leaves the header row from - the last one it has anything to do at
        const lastSortable = _.findLast(columns, ({sortable}) => !!sortable);
        if (lastSortable) {
            lastSortable.suppressHeaderKeyboardEvent = tabOutOfTheHeaderIsNotTheGrids;
        }

        return columns;
    }, [translate, readOnly, editingRowId, headers, dimensionOptions, roleOptions, validate, saveRow,
        cancelEdit, enterEditMode, deleteRow]);

    // a reload of the same company keeps its rows on screen; a first load - and a company change,
    // which drops the rows before it asks - is waited out under the loader
    const showGrid = loadingStatus !== LOADING_STATUS.LOADING || rowData.length > 0;

    // a company with no project units gets no column per unit, and the grid narrows to the role,
    // its approvers and the actions
    const hasUnits = headers.length > 0;

    /**
     * Whether this page has anything to configure at all. A company with no project units can
     * still hold lines - a role and its approvers standing for every project there is - and those
     * are rows like any other: headings over them, and an Add under them.
     *
     * With neither, there is nothing for a heading to name and no line to add: the grid stands as
     * its empty message alone (see HEADLESS_GRID).
     */
    const hasContent = hasUnits || lines.length > 0;
    const noRowsMessage = translate(hasContent ? 'projects.noMatches' : 'generic.noData');

    return (
        // the outer wrapper is what ConfigurationWrapper used to render around every configuration page
        <div>
            <div className="w-100">
                <main className="page w-100 d-flex justify-content-center align-items-start">
                    <div className={`py-16 mb-48 ${hasUnits ? 'w-100 mx-16' : 'w-50'} settings-page__container`}>
                        <h2 className="ps-4 mb-4 d-flex align-items-center">{translate("projects.header")}
                            <span className="ms-4"
                                  data-for={SHARED_TOOLTIP_ID}
                                  data-place={HEADING_INFO_TOOLTIP_PLACE}
                                  data-tip="projects-header-info-tooltip">
                                <CircleQuestionMark size={ICON_SIZE.MEDIUM}/>
                            </span>
                            <Tooltip
                                id="projects-header-info-tooltip"
                                content={() => translate("projects.headerInfo")}
                            />
                        </h2>

                        <div className="bg-white radius-4 p-4 setting_collapsable-main new-projects__card">
                            {showGrid
                                ? <AppGrid<ProjectGridRow>
                                    className={hasContent ? 'new-projects__grid' : HEADLESS_GRID}
                                    dataCy="projects.grid"
                                    containerRef={setGridElement}
                                    height={gridHeight}
                                    rowData={rowData}
                                    columnDefs={columnDefs}
                                    getRowId={(params: GetRowIdParams<ProjectGridRow>) => params.data.id}
                                    rowHeight={ROW_HEIGHT}
                                    getRowHeight={getRowHeight}
                                    // with nothing to configure there is nothing for a heading to
                                    // name, so the header row goes - and what is left of it is
                                    // nothing to tab onto either
                                    headerHeight={hasContent ? HEADER_HEIGHT : 0}
                                    suppressHeaderFocus={!hasContent}
                                    postSortRows={keepDraftLast}
                                    onGridReady={onGridReady}
                                    onKeyDown={onGridKeyDown}
                                    localeText={{noRowsToShow: noRowsMessage}}
                                />
                                : <div className="new-projects__loading" ref={setGridElement}
                                       style={gridHeight ? {height: gridHeight} : undefined}>
                                    <Loader size={LoaderSize.Medium}/>
                                </div>
                            }

                            {!readOnly && hasContent &&
                                <div className="d-flex justify-content-end mt-4">
                                    <Button id={ADD_BUTTON_ID}
                                            type={ButtonType.SECONDARY}
                                            onClick={addRow}
                                            disabled={editingRowId !== null}
                                            text={translate("projects.add")}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

/**
 * Configuration -> Projects. The flag is read here rather than around the route, so the page
 * itself decides which of its two renderings it is.
 */
export const ProjectWrapper = () => {
    const useAgGrid = launchDarkly.userClient?.variation(AG_GRID_IN_CONFIGURATION_FLAG, false) ?? false;

    return useAgGrid ? <NewProjects/> : <Projects/>;
};

export default ProjectWrapper;
